import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Recycle, PackageOpen } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import api from "../lib/api";

export default function CreatorProfile() {
    const { name } = useParams();
    const { data, isLoading, isError } = useQuery({
        queryKey: ["creator", name],
        queryFn: async () => (await api.get(`/creators/name/${encodeURIComponent(name)}`)).data,
        retry: false,
    });

    return (
        <>
            <Navbar />
            <main className="pt-28 md:pt-36 pb-16 md:pb-24 min-h-[70vh]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/"
                        data-testid="creator-back-button"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-white font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2.6} /> Kembali ke Beranda
                    </Link>

                    {isLoading && <div className="mt-8 h-40 rounded-[26px] border-2 border-brand-ink/10 bg-white animate-pulse" />}

                    {!isLoading && isError && (
                        <div className="mt-8 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/70 py-16 text-center">
                            <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                            <p className="mt-4 font-display text-xl font-bold">Kreator tidak ditemukan</p>
                            <p className="mt-1.5 text-sm text-brand-ink/60">Mungkin linknya salah atau kreasinya sudah dipindah.</p>
                        </div>
                    )}

                    {data && (
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 rounded-[30px] border-2 border-brand-ink bg-white p-7 md:p-10 shadow-hard flex flex-col sm:flex-row sm:items-center gap-6"
                        >
                            <span className="w-20 h-20 rounded-3xl bg-brand-carrot text-white grid place-items-center font-display text-4xl font-extrabold border-2 border-brand-ink shadow-hard-sm shrink-0">
                                {data.name?.[0]?.toUpperCase()}
                            </span>
                            <div className="flex-1">
                                <h1 data-testid="creator-profile-name" className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                    {data.name}
                                </h1>
                                {data.bio && <p className="mt-2 text-brand-ink/70 leading-relaxed">{data.bio}</p>}
                                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-ink/60">
                                    <Recycle className="w-4 h-4 text-brand-forest" /> {data.products.length} karya di Acture.id
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {data && data.products.length > 0 && (
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 items-stretch">
                            {data.products.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                    )}

                    {data && data.products.length === 0 && (
                        <div className="mt-10 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/70 py-16 text-center">
                            <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                            <p className="mt-4 font-display text-xl font-bold">Belum ada karya</p>
                            <p className="mt-1.5 text-sm text-brand-ink/60">Kreator ini belum upload karya — pantengin terus ya!</p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
